import React, { FC, useMemo } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useIsMDMediaQuery } from "@app/hooks";
import { TableData } from "./types";
import { parsePercentsFromNumeric, removeYearFromDateString } from "./helpers";

type Props = {
  data: TableData[];
  dateFrom: string;
  dateTo: string;
};

export const StatisticsTable: FC<Props> = ({ data, dateFrom, dateTo }) => {
  const { t } = useTranslation("performance-graphs");
  const isMDScreen = useIsMDMediaQuery();
  const mobileData = useMemo(
    () =>
      data.map((rowData) => ({
        label: rowData.statistics,
        values: Object.entries(rowData).filter(
          (property) => property[0] !== "statistics",
        ),
      })),
    [],
  );

  return (
    <div className={classNames("text-regular")}>
      {!isMDScreen ? (
        mobileData.map((rowData) => (
          <div
            key={rowData.label}
            className={classNames("mb-10", "last-of-type:mb-0")}
          >
            <h5 className={classNames("mb-2.5 text-primary-content")}>
              {t(`statisticsTableColumnTitles.${rowData.label}`, {
                keySeparator: ".",
                asOfDate: removeYearFromDateString(dateTo),
              })}
            </h5>
            {rowData.values.map((entry) => (
              <div
                key={entry[0]}
                className={classNames(
                  "border-t border-solid border-divider py-2.5 text-extra-small text-base-400",
                  "last-of-type:pb-0",
                )}
              >
                <p>
                  {t(`statisticsTableColumnTitles.${entry[0]}`, {
                    keySeparator: ".",
                  })}
                </p>
                <p>
                  {typeof entry[1] === "number"
                    ? parsePercentsFromNumeric(entry[1])
                    : entry[1]}
                </p>
              </div>
            ))}
          </div>
        ))
      ) : (
        <>
          <div
            className={classNames(
              "grid grid-cols-4 gap-x-6 pb-5 text-primary-content",
              "lg:gap-x-9 lg:pb-6",
            )}
          >
            {Object.keys(data[0]).map((key) => (
              <div key={key}>
                {t(`statisticsTableColumnTitles.${key}`, { keySeparator: "." })}
              </div>
            ))}
          </div>
          {data.map((rowData) => (
            <div
              key={rowData.statistics}
              className={classNames(
                "grid grid-cols-4 gap-x-6 border-t border-divider py-5 text-base-400 last-of-type:pb-0",
                "lg:gap-x-9 lg:py-6",
              )}
            >
              {Object.values(rowData).map((column, index) => (
                <div key={column}>
                  {index === 0
                    ? t(`statisticsTableColumnTitles.${column}`, {
                        keySeparator: ".",
                        asOfDate: removeYearFromDateString(dateTo),
                      })
                    : typeof column === "number"
                      ? parsePercentsFromNumeric(column)
                      : column}
                </div>
              ))}
            </div>
          ))}
        </>
      )}

      <p
        className={classNames(
          "mt-10 text-extra-small text-neutral-content",
          "xxl:mt-15",
        )}
      >
        {t(`footNote`, {
          dateFrom: dateFrom,
          dateTo: dateTo,
        })}
      </p>
    </div>
  );
};
